import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

export default function Hero() {
  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1771790193799-2ee0163f2e87?q=80&w=2070&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-dark/90 via-dark/80 to-dark" />
      </div>

      {/* Animated grid pattern */}
      <div className="absolute inset-0 z-0 opacity-20">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `linear-gradient(rgba(186, 242, 101, 0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(186, 242, 101, 0.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1
            data-testid="hero-headline"
            className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase tracking-tight leading-tight"
          >
            <span className="text-white">We Scale Brands with</span>
            <br />
            <span className="text-lime">Social Media,</span>
            <span className="text-white"> Paid Ads</span>
            <br />
            <span className="text-white">& </span>
            <span className="text-lime">AI Automation.</span>
          </h1>
        </motion.div>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-body text-base md:text-lg text-white/70 max-w-2xl mx-auto mt-8 leading-relaxed"
        >
          Social-first marketing, content creation, and automation that actually
          drives revenue — not just likes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-10"
        >
          <button
            onClick={scrollToContact}
            data-testid="hero-cta-btn"
            className="inline-flex items-center gap-3 bg-lime text-black font-body font-semibold text-base px-8 py-4 hover:bg-lime-hover transition-all duration-300 hover:gap-4 lime-glow"
          >
            GET STARTED
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20 flex flex-wrap justify-center gap-8 md:gap-16"
        >
          {[
            { value: "250+", label: "Brands Scaled" },
            { value: "100M+", label: "Impressions Generated" },
            { value: "2", label: "Locations" },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="font-heading font-bold text-3xl md:text-4xl text-lime">
                {stat.value}
              </div>
              <div className="font-body text-sm text-white/50 mt-1">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1.5 h-1.5 bg-lime rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
}
